import { useState, useEffect } from "react";

import {
  Button,
  Modal,
  CloseSVG,
  Checkbox,
  useModal,
  ChevronLeftSVG,
  Icon,
  DotFilledSVG,
} from "@newsela/angelou";

import {
  checkboxCss,
  closeButtonCss,
  footerButtonsWrapper,
  footerNextButton,
  modalContentImage,
  modalContentImageWrapper,
  modalPagination,
  modalContentText,
  modalContentWrapper,
  modalCss,
  modalFooterCss,
  modalTitle,
  modalWrapper,
  tittleWrapper,
  modalPaginationWrapper,
} from "./GuidedHighlightingStudentInfoPopup.style";

interface IProps {
  active: boolean;
  setActive: (active: boolean) => void;
}

const pages = [
  {
    page: 0,
    title: "Welcome",
    image_url: "/images/guided-highlights/welcome_illustration.svg",
    heading_text: "Get started with guided highlighting!",
    paragraph_text:
      "Achieve deeper comprehension by practicing close reading and highlighting.",
  },
  {
    page: 1,
    title: "Instructions",
    image_url: "/images/guided-highlights/instructions_illustration.svg",
    heading_text: "Read the prompt, then highlight the text",
    paragraph_text:
      "You get 10 points if you highlight the correct section on your first try, and 5 if you get it on the second. If you miss both, we'll show you where to highlight.",
  },
];

const GuidedHighlightingStudentInfoPopup = ({ active, setActive }: IProps) => {
  const [modalProps] = useModal(true);
  const [currentPage, setCurrentPage] = useState(pages[0]);
  const [isChecked, setIsChecked] = useState(false);
  const firstPage = pages[0];
  const lastPage = pages[pages.length - 1];

  useEffect(() => {
    const storagedCheckBoxValue = localStorage.getItem(
      "skip_guidedHighlighting_popup",
    );

    storagedCheckBoxValue ? setIsChecked(true) : setIsChecked(false);
  }, []);

  const handleNextPage = () => {
    if (currentPage?.page !== lastPage?.page) {
      const activePagePosition = currentPage?.page || 0;
      const activePage = activePagePosition + 1;

      setCurrentPage(pages[activePage]);
    }
  };

  const handleBackPage = () => {
    if (currentPage?.page !== firstPage?.page) {
      const activePagePosition = currentPage?.page || 1;
      const activePage = activePagePosition - 1;

      setCurrentPage(pages[activePage]);
    }
  };

  const renderPagination = () => {
    return pages.map((page) => (
      <div
        css={modalPagination(page.page === currentPage?.page)}
        key={page.page}
      >
        <Icon
          isDecorative={false}
          size="MD"
          SvgComponent={DotFilledSVG}
          title="Pagination dot"
        />
      </div>
    ));
  };

  const setDoNotShowAgain = (event: { target: { checked: boolean } }) => {
    setIsChecked(!isChecked);
    if (event.target.checked) {
      localStorage.setItem("skip_guidedHighlighting_popup", "true");
    } else {
      localStorage.removeItem("skip_guidedHighlighting_popup");
    }
  };

  const closePopup = () => {
    setActive(false);
    setCurrentPage(pages[0]);
  };

  return (
    <Modal
      {...modalProps}
      onBackgroundClick={() => setActive(false)}
      isActive={active}
      __cssFor={{ root: modalCss }}
      ariaProps={{
        "aria-labelledby": "popup_modal_label",
      }}
    >
      <div css={modalWrapper}>
        <div css={tittleWrapper}>
          <h2 css={modalTitle}>{currentPage?.title}</h2>
          <Button
            __cssFor={{ root: closeButtonCss }}
            flavor="INCOGNITO"
            ariaProps={{
              "aria-label": "Close Modal Button",
            }}
            icon={{ SvgComponent: CloseSVG }}
            onClick={() => closePopup()}
          />
        </div>
        <div css={modalContentWrapper}>
          <div css={modalContentImageWrapper}>
            <img
              css={modalContentImage}
              src={currentPage?.image_url}
              alt={currentPage?.title.concat(" illustration")}
            />
          </div>
          <div css={modalContentText}>
            <h1>{currentPage?.heading_text}</h1>
            <p>{currentPage?.paragraph_text}</p>
            {currentPage?.page === lastPage?.page && (
              <Checkbox
                label="Do not show again"
                name="class"
                onChange={(event) => {
                  setDoNotShowAgain(event);
                }}
                checked={isChecked}
                __cssFor={{ root: checkboxCss }}
              />
            )}
          </div>
        </div>
        <div css={modalPaginationWrapper}>{renderPagination()}</div>
        <div css={modalFooterCss}>
          <div css={footerButtonsWrapper}>
            {currentPage?.page !== lastPage?.page && (
              <Button
                __cssFor={{ root: footerNextButton }}
                flavor={Button.flavors.primary}
                onClick={() => handleNextPage()}
                ariaProps={{
                  "aria-label": "Next Button",
                }}
              >
                Next
              </Button>
            )}
            {currentPage?.page !== firstPage?.page && (
              <Button
                flavor={Button.flavors.tertiary}
                onClick={() => handleBackPage()}
                ariaProps={{
                  "aria-label": "Back Button",
                }}
              >
                <Icon SvgComponent={ChevronLeftSVG} size={Icon.sizes.sm} />
                Back
              </Button>
            )}
            {currentPage?.page === lastPage?.page && (
              <Button
                __cssFor={{ root: footerNextButton }}
                flavor={Button.flavors.primary}
                onClick={() => closePopup()}
                ariaProps={{
                  "aria-label": "Get Started Button",
                }}
              >
                Get Started!
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GuidedHighlightingStudentInfoPopup;
